import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { MatDialog } from '@angular/material/dialog';
import {
  WeeklyHoursService,
  WeeklyHoursReportSummary,
  WeeklyHoursConfig,
  HoursSubmissionSummary,
  HoursSubmissionDetail
} from '../../../services/weekly-hours.service';
import { AuthService } from '../../../services/auth.service';
import { FlagService } from '../../../shared/services/flag.service';
import { FlagType } from '../../../shared/utils/enums';
import { HoursSubmissionDialogComponent } from './hours-submission-dialog.component';

@Component({
  selector: 'app-weekly-hours-list',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, TooltipModule, FormsModule],
  templateUrl: './weekly-hours-list.component.html',
  styleUrl: './weekly-hours-list.component.scss'
})
export class WeeklyHoursListComponent implements OnInit {
  reports: WeeklyHoursReportSummary[] = [];
  loading = false;
  sendingId: number | null = null;

  // E1 : config périodicité + e-mail comptable
  config: WeeklyHoursConfig | null = null;
  savingConfig = false;

  // E4 : relevés d'heures envoyés par les pros (en attente de validation)
  submissions: HoursSubmissionSummary[] = [];
  submissionsLoading = false;
  validatingId: number | null = null;

  constructor(
    private service: WeeklyHoursService,
    private router: Router,
    private route: ActivatedRoute,
    private flag: FlagService,
    private auth: AuthService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.load();
    this.loadConfig();
    this.loadSubmissions();
  }

  load(): void {
    this.loading = true;
    this.service.list().subscribe({
      next: (data) => {
        this.reports = data ?? [];
        this.loading = false;
      },
      error: () => {
        this.flag.displayFlag('Erreur lors du chargement des relevés', FlagType.error);
        this.loading = false;
      }
    });
  }

  // ────────────────────────────────────────────────────────────
  // E1 : configuration
  // ────────────────────────────────────────────────────────────

  loadConfig(): void {
    const agencyId = this.auth.agencyId;
    if (!agencyId) return;
    this.service.getConfig(agencyId).subscribe({
      next: (cfg) => {
        this.config = { ...cfg, accountantEmail: cfg.accountantEmail ?? '' };
      },
      error: () => {
        // Pas de config : défauts affichés
        this.config = { agencyId: agencyId!, frequency: 'WEEKLY', accountantEmail: '' };
      }
    });
  }

  saveConfig(): void {
    if (!this.config) return;
    this.savingConfig = true;
    this.service.updateConfig(this.config).subscribe({
      next: () => {
        this.flag.displayFlag('Paramètres enregistrés', FlagType.success);
        this.savingConfig = false;
      },
      error: () => {
        this.flag.displayFlag('Erreur lors de l\'enregistrement', FlagType.error);
        this.savingConfig = false;
      }
    });
  }

  // ────────────────────────────────────────────────────────────
  // E4 : validations des relevés d'heures
  // ────────────────────────────────────────────────────────────

  /** Lien depuis la notification « Relevé d'heures à valider » : ouvre directement la soumission de la mission (?submission=ID). */
  private openSubmissionFromLink(): void {
    const id = Number(this.route.snapshot.queryParamMap.get('submission'));
    if (!id) {
      return;
    }
    const target = this.submissions.find((s) => Number(s.missionId) === id);
    // une seule ouverture par lien : on retire le paramètre pour qu'un rafraîchissement ne rouvre pas la boîte
    this.router.navigate([], { relativeTo: this.route, queryParams: { submission: null }, queryParamsHandling: 'merge', replaceUrl: true });
    if (target) {
      this.openSubmission(target);
    }
  }

  loadSubmissions(): void {
    this.submissionsLoading = true;
    this.service.listSubmissions(this.auth.agencyId ?? undefined).subscribe({
      next: (data) => {
        this.submissions = data ?? [];
        this.submissionsLoading = false;
        this.openSubmissionFromLink();
      },
      error: () => {
        this.submissionsLoading = false;
      }
    });
  }

  openSubmission(s: HoursSubmissionSummary): void {
    this.dialog.open(HoursSubmissionDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: { missionId: s.missionId }
    }).afterClosed().subscribe(() => this.loadSubmissions());
  }

  quickValidate(s: HoursSubmissionSummary, event: MouseEvent): void {
    event.stopPropagation();
    this.validatingId = s.missionId;
    this.service.validateSubmission(s.missionId).subscribe({
      next: () => {
        this.flag.displayFlag('Relevé d\'heures validé', FlagType.success);
        this.validatingId = null;
        this.loadSubmissions();
      },
      error: () => {
        this.flag.displayFlag('Erreur lors de la validation', FlagType.error);
        this.validatingId = null;
      }
    });
  }

  open(report: WeeklyHoursReportSummary): void {
    this.router.navigate(['/agency/weekly-hours', report.id]);
  }

  send(report: WeeklyHoursReportSummary, event: MouseEvent): void {
    event.stopPropagation();
    if (!report.canSendNow) return;
    this.sendingId = report.id;
    this.service.send(report.id).subscribe({
      next: () => {
        this.flag.displayFlag('Relevé envoyé', FlagType.success);
        this.sendingId = null;
        this.load();
      },
      error: (err) => {
        this.sendingId = null;
        if (err?.status === 410) {
          // Relevé retiré : plus aucune affectation à rapporter (missions supprimées / pros désaffectés)
          this.flag.displayFlag(err?.error?.message || 'Ce relevé n\'a plus aucune ligne : il a été retiré.', FlagType.warning);
          this.load();
          return;
        }
        this.flag.displayFlag('Erreur lors de l\'envoi', FlagType.error);
      }
    });
  }

  statusLabel(s: string): string {
    return s === 'SENT' ? 'Envoyé' : s === 'READY' ? 'Prêt' : 'Brouillon';
  }
}
